import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  MapPin, 
  Briefcase,
  CheckCircle2,
  Calendar,
  HelpCircle,
  GraduationCap,
  Building2,
  Heart,
  Users,
} from 'lucide-react';

const CentralTexasSocialWorkJobs = () => {
  useEffect(() => {
    // Set SEO meta tags
    document.title = "How to Find Social Work Jobs in Central Texas (2026 Guide)";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "Looking for social work jobs in Central Texas? Explore opportunities in Austin, Waco, Temple, and Killeen for LMSW and LCSW professionals in 2026.";
  }, []);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Article Header */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/blog" className="hover:text-primary">Blog</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-700">Central Texas Social Work Jobs</span>
        </nav>

        {/* Meta info */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>2026 Guide</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            <span>Central Texas</span>
          </div>
        </div>

        {/* H1 - Blog Title */}
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900 mb-8 leading-tight">
          How to Find Social Work Jobs in Central Texas (2026 Guide)
        </h1>

        {/* Introduction */}
        <div className="prose prose-lg max-w-none text-gray-700 mb-10">
          <p>
            The demand for licensed social workers in Central Texas continues to grow in 2026. With expanding healthcare systems, increasing school support needs, and a rising demand for hospice and behavioral health services, opportunities for LMSWs and LCSWs are steadily increasing across Austin, Waco, Temple, Killeen, and surrounding communities.
          </p>
          <p>
            If you are searching for social work jobs in Central Texas, this guide will help you understand where to apply, which cities are hiring, and how to position yourself competitively in today's job market.
          </p>
        </div>

        {/* Section: Types of Social Work Jobs */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">
            Types of Social Work Jobs in Central Texas
          </h2>
          <p className="text-gray-700 mb-4">
            Central Texas offers a variety of employment settings for licensed social workers, including:
          </p>
          <ul className="space-y-3 text-gray-700 mb-4">
            <li className="flex items-start gap-2">
              <Building2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span>Hospital and Medical Social Worker positions</span>
            </li>
            <li className="flex items-start gap-2">
              <Heart className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span>Hospice and Home Health Social Work roles</span>
            </li>
            <li className="flex items-start gap-2">
              <GraduationCap className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span>School Social Worker openings</span>
            </li>
            <li className="flex items-start gap-2">
              <Users className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span>Behavioral Health and Mental Health Services</span>
            </li>
            <li className="flex items-start gap-2">
              <Briefcase className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span>Government and Community-Based Agencies</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span>Nursing and Long-Term Care Facilities</span>
            </li>
          </ul>
          <p className="text-gray-700">
            Austin's hospital systems, Waco's healthcare providers, and Temple's growing medical community create consistent opportunities for qualified professionals.
          </p>
        </section>

        {/* Section: Best Cities */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">
            Best Cities in Central Texas for Social Workers
          </h2>
          <p className="text-gray-700 mb-4">
            Several cities in Central Texas offer strong employment prospects:
          </p>
          <div className="bg-gray-50 rounded-xl p-6 space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-gray-900">Austin</p>
                <p className="text-gray-600 text-sm">High demand for hospital, behavioral health, and school social workers.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-gray-900">Temple</p>
                <p className="text-gray-600 text-sm">Major healthcare hub with medical and hospice roles.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-gray-900">Waco</p>
                <p className="text-gray-600 text-sm">Growing healthcare and school district needs.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-gray-900">Killeen</p>
                <p className="text-gray-600 text-sm">Military-adjacent services and community agencies.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-gray-900">Belton</p>
                <p className="text-gray-600 text-sm">Expanding healthcare and long-term care facilities.</p>
              </div>
            </div>
          </div>
          <p className="text-gray-700 mt-4">
            Focusing your search by city can improve your chances of finding specialized openings.
          </p>
        </section>

        {/* Section: LMSW vs LCSW */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">
            LMSW vs LCSW Opportunities in Central Texas
          </h2>
          <p className="text-gray-700 mb-4">
            Both LMSW and LCSW professionals are needed across Central Texas.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-4">
            <div className="bg-blue-50 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-3">LMSWs commonly find positions in:</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600" />
                  Hospitals
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600" />
                  Hospice agencies
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600" />
                  Schools
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600" />
                  Case management roles
                </li>
              </ul>
            </div>
            
            <div className="bg-green-50 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-3">LCSWs often qualify for:</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  Clinical leadership positions
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  Private practice
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  Supervisory roles
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  Behavioral health programs
                </li>
              </ul>
            </div>
          </div>
          
          <p className="text-gray-700">
            Understanding your license level helps you target appropriate job listings and salary ranges.
          </p>
        </section>

        {/* Section: Tips to Stand Out */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">
            Tips to Stand Out in the Central Texas Job Market
          </h2>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Tailor your resume to healthcare, school, or hospice settings.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Highlight Texas licensure clearly at the top of your resume.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Include measurable outcomes in your experience.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Prepare examples of interdisciplinary collaboration.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Maintain continuing education compliance.</span>
            </li>
          </ul>
          <p className="text-gray-700 mt-4">
            Employers in Central Texas value practical experience, cultural competence, and strong communication skills.
          </p>
        </section>

        {/* Section: FAQ */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-5">
              <div className="flex items-start gap-2 mb-2">
                <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="font-semibold text-gray-900">Are social workers in demand in Central Texas?</p>
              </div>
              <p className="text-gray-700 pl-7">Yes. Healthcare expansion and school-based services continue to increase demand.</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-5">
              <div className="flex items-start gap-2 mb-2">
                <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="font-semibold text-gray-900">What is the average salary for social workers in Central Texas?</p>
              </div>
              <p className="text-gray-700 pl-7">Salaries vary by city and license level, but clinical and healthcare roles often offer competitive compensation.</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-5">
              <div className="flex items-start gap-2 mb-2">
                <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="font-semibold text-gray-900">Which city in Central Texas hires the most social workers?</p>
              </div>
              <p className="text-gray-700 pl-7">Austin and Temple typically have the highest volume of openings due to large healthcare systems.</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-5">
              <div className="flex items-start gap-2 mb-2">
                <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="font-semibold text-gray-900">Is Central Texas a good place to grow a social work career?</p>
              </div>
              <p className="text-gray-700 pl-7">Yes. The region offers diverse settings including medical, educational, behavioral health, and community services.</p>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-10">
          <p className="text-gray-700 text-lg leading-relaxed">
            Finding social work jobs in Central Texas requires a focused approach. By targeting the right cities, understanding your license qualifications, and using specialized employment platforms, you can position yourself for success in 2026.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed mt-4">
            As the Central Texas region continues to grow, opportunities for licensed social workers are expected to expand across healthcare, schools, and community agencies.
          </p>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-primary to-[#0F2E24] rounded-xl p-8 text-center">
          <h3 className="font-serif text-2xl font-bold text-white mb-4">
            Are you currently searching for social work positions in Central Texas?
          </h3>
          <p className="text-gray-300 mb-6">
            Connect with us and browse social work jobs in Austin, Waco, Temple, Killeen, and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/jobs?location=Texas">
              <Button className="bg-secondary hover:bg-secondary-hover text-white px-8" data-testid="browse-texas-cta">
                Browse Texas Jobs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8">
                Connect With Us
              </Button>
            </Link>
          </div>
        </section>
      </article>
    </div>
  );
};

export default CentralTexasSocialWorkJobs;
